<template>
    <div v-if="wavelineOverview" v-loading="loading">

        <div class="row">
            <div class="col-xl-2 col-lg-3 col-md-6">
                <fg-input class="btn-one-size-dropdown btn-one-size">
                    <el-select size="large"
                               class="select-primary"
                               :placeholder="$t('Time frame')"
                               fullWidth
                               @input="setMonthsFilter"
                               :value="months">
                        <el-option v-for="option in monthsOptions"
                                   :value="option.value"
                                   :label="$t(option.label)"
                                   :key="option.value">
                        </el-option>
                    </el-select>
                </fg-input>
            </div>
        </div>

        <div class="header text-center">
            <h3 class="title">{{$t('Waveline')}}</h3>
        </div>

        <div v-if="checkPackage(['advanced', 'premium'])" class="row">
            <div class="col-lg-12 col-sm-12">
                <user-waveline-chart :title="$t('Development Team and Members')" :data="teamDiff"/>
            </div>
        </div>

        <h5 class="mt-3">{{ this.$t('Performance Trend')}}</h5>
        <div class="row pull-center mr-0 ml-0">
            <div v-if="wavelineData" :class="tasks.length > 0 ? 'el-col-lg-11 el-col-md-20 el-col-sm-20 el-col-xs-20' : 'col-lg-6 col-md-12 p-0'">
                <vue-funnel-graph :labels="wavelineData.labels" :width="width"
                                  :height="wavelineData.values.length * taskHeight"
                                  :values="wavelineData.values" :sub-labels="wavelineData.subLabels"
                                  :direction="'vertical'"
                                  :gradient-direction="'vertical'"
                                  :animated="true" :display-percentage="false"
                />
            </div>
            <div v-if="tasks.length > 0" class="el-col-lg-2 el-col-md-4 el-col-sm-4 el-col-xs-4 mt-2" :style="`transform: translateY(-${taskHeight / 2}px)`">
                <div v-for="(item, i) in tasks" class="waveline-pull-center"
                     :style="`min-height: ${taskHeight}px; max-height: ${taskHeight}px;`">
                    <div v-if="item" class="row">
                        <div class="col-lg-12 waveline-pull-center">
                            <p-button type="success" round icon class="btn-icon-mini p-0 m-0"
                                      @click="onNoteClick(item)">
                                <i v-if="item.note" class="fa fa-comment-alt"/>
                                <i v-else class="far fa-comment-alt"/>
                            </p-button>
                        </div>
                        <div class="col-lg-12 p-1 waveline-pull-center hidden-lg-and-up">
                            <p-button v-if="item.createdTasks.length > 0 || item.completedTasks.length > 0" type="success" round icon class="btn-icon-mini p-0 m-0"
                                      @click="onTasksClick(item)">
                                <i class="fas fa-tasks"/>
                            </p-button>
                        </div>
                        <div class="col-lg-12 p-1 waveline-pull-center">
                          <div v-if="item.alertActive" class="d-inline-flex justify-content-around alert-indicator">
                            <div  class="alert-indicator-cell">
                              <i class="fa fa-2x fa-exclamation-triangle" style="vertical-align: -5px;"/>
                            </div>
                          </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="tasks.length > 0" class="el-col-lg-11 el-col-md-11 hidden-md-and-down mt-2" :style="`transform: translateY(-${taskHeight / 2}px)`">
                <div v-for="(item, i) in tasks" :style="`min-height: ${taskHeight}px; max-height: ${taskHeight}px;`">
                    <task-list v-if="item" v-model="tasks[i]" :height="taskHeight"/>
                </div>
            </div>
        </div>

        <modal v-model="showNote" :submit="updateNote" :cancel="cancel" submit-text="Save" :disabled="note === ''"
               :title="$t('Note')">
            <template slot="body">
                <el-input type="textarea"
                          :placeholder="$t('Note')"
                          :autosize="{ minRows: 3, maxRows: 4}"
                          v-model="note"/>
            </template>
        </modal>

        <el-dialog :visible.sync="showTasks"
                  width="95%">
            <h2 class="mb-0">{{$t('Tasks')}}</h2>
            <card class="card-plain">
                <div class="card-content text mb-2">
                    <task-list v-model="task"/>
                </div>
            </card>
        </el-dialog>
    </div>
</template>

<script>
  import { Input, Select, Option, Dialog } from 'element-ui'
  import { TimeLine, TimeLineItem, Badge } from 'src/components/UIComponents'
  import Modal from '../../Dashboard/Components/BasicModal'
  import StatusIndicator from '../../Dashboard/Components/StatusIndicator'
  import moment from 'moment'
  import UserWavelineChart from '../../Dashboard/Components/UserWavelineChart'
  import ChartCard from 'src/components/UIComponents/Cards/ChartCard'
  import VueFunnelGraph from '../../UIComponents/Charts/FunnelGraph/FunnelGraph'
  import { mapActions, mapGetters, mapState } from 'vuex'
  import List from '../../Dashboard/Components/BasicList'
  import TaskList from './TaskList'
  import PButton from '../../UIComponents/Button'

  export default {
    name: 'WaveLine',
    components: {
      PButton,
      [Input.name]: Input,
      [Select.name]: Select,
      [Option.name]: Option,
      [Dialog.name]: Dialog,
      StatusIndicator,
      TimeLine,
      TimeLineItem,
      Badge,
      Modal,
      VueFunnelGraph,
      UserWavelineChart,
      ChartCard,
      List,
      TaskList
    },
    data () {
      return {
        taskHeight: 150,
        width: 300,
        loading: false,
        showNote: false,
        showTasks: false,
        note: null,
        task: null,
        queryId: null
      }
    },
    mounted () {
      this.init()
    },
    computed: {
      ...mapGetters({
        teamDiff: 'user/waveline/teamDiff',
        wavelineData: 'user/waveline/wavelineData',
        tasks: 'user/waveline/tasks'
      }),
      ...mapState('user/waveline/teamsByQuestionnaire', {
        questionnaire: state => state.questionnaireObj
      }),
      ...mapState('user/waveline', {
        wavelineOverview: state => state.wavelineOverview,
        months: state => state.months,
        monthsOptions: state => state.monthsOptions,
      }),
      team: function () {
        return this.wavelineOverview && this.wavelineOverview.team || {}
      },
      user: function () {
        return this.wavelineOverview && this.wavelineOverview.user || {}
      },
      breadcrumb: function () {
        const breadcrumb = []

        const { qid, tid, uid } = this.$route.params
        if (this.questionnaire) {
          breadcrumb.push({ name: 'WavelineQuestionnaireSelection', label: this.$options.filters.languageString(this.questionnaire.title) })
        }

        if (this.$route.name === 'WavelineUserView') {
          if (tid && this.team) {
            breadcrumb.push({ name: 'WavelineTeamSelection', params: { qid }, label: this.team.title })
          }
          if (uid && this.user) {
            breadcrumb.push({ name: 'WavelineUserSelection', params: { qid, tid, uid }, label: this.$options.filters.formatDisplayName(this.user) })
          }
        } else {
          if (tid && this.team) {
            breadcrumb.push({ name: 'WavelineTeamSelection', params: { qid }, label: this.team.title })
          }
        }

        breadcrumb.push({ label: 'Waveline' })

        return breadcrumb
      }
    },
    methods: {
      ...mapActions('user/waveline', ['getWavelineOverview', 'setMonthsFilter', 'setNote', 'initPackageMonthsOptions']),
      async init () {
        try {
          this.loading = true
          this.initPackageMonthsOptions(this.$auth.user.customer.settings.contract.packageType)
          await this.getWavelineOverview({
            questionnaireId: this.$route.params.qid,
            teamId: this.$route.params.tid,
            userId: this.$route.params.uid,
            months: this.months
          })
          this.loading = false
        } catch (e) {
          this.loading = false
        }
      },
      dateFormatter: function (item) {
        return moment(item.createdAt).fromNow()
      },
      onTasksClick: function (item) {
        this.task = item
        this.showTasks = true
      },
      onNoteClick: function (item) {
        this.queryId = item.query
        this.note = item.note
        this.showNote = true
      },
      updateNote: async function () {
        const questionnaireId = this.$route.params.qid
        const teamId = this.$route.params.tid
        const userId = this.$route.params.uid
        const queryId = this.queryId

        try {
          await this.setNote({ questionnaireId, teamId, userId, queryId, item: { note: this.note } })
          this.$notify({ message: this.$t('Successfully saved.'), type: 'success' })
        } catch (e) {
          this.$notify({ message: e.message, type: 'danger' })
        }
        this.showNote = false
      },
      cancel: function () {
        this.showNote = false
      },
      checkPackage(allowedPackages) {
        return allowedPackages.includes(this.$auth.user.customer.settings.contract.packageType)
      }
    },
    watch: {
      months (val) {
        if (val) {
          this.init()
        }
      },
      breadcrumb () {
        this.$store.commit('breadcrumb/SET_ROUTE_DATA', {
          path: this.$route.path,
          breadcrumb: this.breadcrumb
        }, { root: true })
      }
    }
  }
</script>

<style lang="scss">
    .card-timeline .timeline-heading {
        margin-bottom: 0;
    }

    .svg-funnel-js__subLabel--title {
        color: black;
    }

    .waveline-pull-center {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .modal-body {
      padding: 16px;
    }

    @media (max-width: 590px) {
      .btn-one-size-dropdown {
          width: 100%;
          padding-left: 0px;
          padding-right: 0px;
          padding-right: 10px !important;
      }
    }

    .alert-indicator {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      display: inline-block;
      min-width: 40px;
      max-width: 40px;
      height: 40px;
      line-height: 40px;
      margin: 1px 1px;
      padding: 0 15px;
      &.full-width {
        width: 100%;
      }
      &-cell {
        display: inline-block;
        padding: 0 4px;
        color: black;
        text-align: center;
        vertical-align: -5px !important;
        color: #ff0000 !important;
      }
      :last-child {
        text-align: left;
      }
    }
</style>
